import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UserState {
  email: string;
  phone_number: string;
  first_name: string;
  last_name: string;
  id: number;
  role_name: string;
  role_id: number;
  specialty?: string;
  organization?: string;
  is_active?: boolean;
  recognitionType?: 'azure' | 'qwen';
  avatar_url?: string | null; // URL аватара (например: "/avatars/user_60_f74acdfd.webp")
  [key: string]: any; // Для дополнительных полей из API
}

type UserSliceState = UserState | null;

const initialState: UserSliceState = (() => {
  const user = localStorage.getItem('user');
  const storedRecognition = localStorage.getItem('recognition_type');
  
  // При инициализации просто возвращаем пользователя из localStorage
  // Проверка токенов будет происходить в App.tsx
  if (user) {
    try {
      const parsed = JSON.parse(user) as UserState;
      if (storedRecognition === 'azure' || storedRecognition === 'qwen') {
        parsed.recognitionType = storedRecognition as any;
      }
      return parsed;
    } catch (error) {
      console.error('Ошибка при парсинге пользователя:', error);
      localStorage.removeItem('user');
      return null;
    }
  }
  
  // Если пользователя нет, но есть сохраненный тип распознавания — вернем минимальный объект
  if (storedRecognition === 'azure' || storedRecognition === 'qwen') {
    return {
      email: '',
      phone_number: '',
      first_name: '',
      last_name: '',
      id: 0,
      role_name: '',
      role_id: 0,
      recognitionType: storedRecognition as any,
    } as UserState;
  }

  return null;
})();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state: UserSliceState, action: PayloadAction<UserState>) => {
      // Сохраняем выбранный тип распознавания при обновлении пользователя
      let persistedRecognition: 'azure' | 'qwen' | undefined = undefined;
      try {
        const fromKey = localStorage.getItem('recognition_type');
        if (fromKey === 'azure' || fromKey === 'qwen') persistedRecognition = fromKey;
        if (!persistedRecognition) {
          const stored = localStorage.getItem('user');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed && (parsed.recognitionType === 'azure' || parsed.recognitionType === 'qwen')) {
              persistedRecognition = parsed.recognitionType;
            }
          }
        }
      } catch {}

      const nextUser: UserState = {
        ...action.payload,
        recognitionType: action.payload.recognitionType || state?.recognitionType || persistedRecognition || 'qwen'
      } as UserState;

      localStorage.setItem('user', JSON.stringify(nextUser));
      if (nextUser.recognitionType) {
        localStorage.setItem('recognition_type', nextUser.recognitionType);
      }
      return nextUser;
    },
    clearUser: () => {
      localStorage.removeItem('user');
      return null;
    },
    updateUserActivationStatus: (state: UserSliceState, action: PayloadAction<{ is_active: boolean }>) => {
      if (state) {
        state.is_active = action.payload.is_active;
        localStorage.setItem('user', JSON.stringify(state));
      }
      return state;
    },
    setRecognitionType: (state: UserSliceState, action: PayloadAction<'azure' | 'qwen'>) => {
      if (state) {
        state.recognitionType = action.payload;
        localStorage.setItem('user', JSON.stringify(state));
        localStorage.setItem('recognition_type', action.payload);
      }
      return state || null;
    },
  },
});

export const { setUser, clearUser, updateUserActivationStatus, setRecognitionType } = userSlice.actions;
export default userSlice.reducer; 